import * as React from 'react'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

import { ReportType } from '@/app/constants/reportType'

export const GENERATED_REPORTS_LIMIT = 50

type State = {
  generatedReports: ReportType[]
  lastGeneratedReports: ReportType[]
  ownerCompanyId: string | undefined
  ownerSubjectsKey: string | undefined
}

type Actions = {
  markGenerated: (reports: ReadonlyArray<ReportType>) => void
  clearGenerated: () => void
  syncCompany: (companyId: string) => void
  syncSubjects: (subjectsKey: string) => void
}

const EMPTY: ReportType[] = []

export const buildSubjectsKey = (
  checkedDrivers: ReadonlyArray<string>,
  checkedVehicles: ReadonlyArray<string>,
): string => {
  if (checkedDrivers.length === 0 && checkedVehicles.length === 0) return ''
  return `d:${[...checkedDrivers].sort().join(',')}|v:${[...checkedVehicles]
    .sort()
    .join(',')}`
}

export const useGeneratedReportsStore = create<State & Actions>()(
  devtools(
    persist(
      (set) => ({
        generatedReports: EMPTY,
        lastGeneratedReports: EMPTY,
        ownerCompanyId: undefined,
        ownerSubjectsKey: undefined,

        markGenerated: (reports) =>
          set(
            (s) => {
              if (reports.length === 0) return {}
              const fresh = Array.from(new Set(reports))
              const added = new Set(fresh)
              return {
                lastGeneratedReports: fresh,
                generatedReports: [
                  ...fresh,
                  ...s.generatedReports.filter((r) => !added.has(r)),
                ].slice(0, GENERATED_REPORTS_LIMIT),
              }
            },
            false,
            'markGenerated',
          ),

        clearGenerated: () =>
          set(
            { generatedReports: EMPTY, lastGeneratedReports: EMPTY },
            false,
            'clearGenerated',
          ),

        syncCompany: (companyId) =>
          set(
            (s) =>
              s.ownerCompanyId === companyId
                ? {}
                : {
                    ownerCompanyId: companyId,
                    ownerSubjectsKey: undefined,
                    generatedReports: EMPTY,
                    lastGeneratedReports: EMPTY,
                  },
            false,
            'syncCompany',
          ),

        syncSubjects: (subjectsKey) =>
          set(
            (s) =>
              subjectsKey === '' || s.ownerSubjectsKey === subjectsKey
                ? {}
                : {
                    ownerSubjectsKey: subjectsKey,
                    generatedReports: EMPTY,
                    lastGeneratedReports: EMPTY,
                  },
            false,
            'syncSubjects',
          ),
      }),
      {
        name: 'printout-generated-reports',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (s) => ({
          generatedReports: s.generatedReports,
          lastGeneratedReports: s.lastGeneratedReports,
          ownerCompanyId: s.ownerCompanyId,
          ownerSubjectsKey: s.ownerSubjectsKey,
        }),
      },
    ),
    { name: 'EasyTacho', store: 'printout-generated-reports' },
  ),
)

export const useGeneratedReports = () => {
  const {
    generatedReports,
    lastGeneratedReports,
    markGenerated,
    clearGenerated,
    syncCompany,
    syncSubjects,
  } = useGeneratedReportsStore(
    useShallow((s) => ({
      generatedReports: s.generatedReports,
      lastGeneratedReports: s.lastGeneratedReports,
      markGenerated: s.markGenerated,
      clearGenerated: s.clearGenerated,
      syncCompany: s.syncCompany,
      syncSubjects: s.syncSubjects,
    })),
  )

  return {
    generatedReports,
    lastGeneratedReports,
    markReportGenerated: React.useCallback(
      (report: ReportType) => markGenerated([report]),
      [markGenerated],
    ),
    clearGeneratedReports: clearGenerated,
    syncGeneratedCompany: syncCompany,
    syncGeneratedSubjects: syncSubjects,
  }
}
